import type { CallingInstance, SdkToken } from "./webexCalling";

/**
 * What the portal keeps of the Webex Calling SDK for the whole page.
 * The SDK holds the signed-in agent's token in module state (its SDKConnector accepts one webex
 * instance and throws on a second), so there is one instance per page, however often the phone
 * stops and starts. Changing the agent or tearing the phone down needs a page reload.
 */

type Renewer = () => Promise<SdkToken>;

let instance: CallingInstance | null = null;
let pending: Promise<CallingInstance> | null = null;
let registered = false;
let tornDown = false;
let renewer: Renewer | null = null;
let shutdownHook: (() => Promise<void>) | null = null;
let heldTokenDigest: string | null = null;

/** The SDK calls this to renew its token; it reaches the run that is current, and none after a stop. */
export const refreshCallback = async (): Promise<SdkToken> => {
  if (!renewer) throw new Error("Phone is stopped");
  return renewer();
};

/** A new renewer replaces the previous run's; null once the phone stopped. */
export const setRenewer = (next: Renewer | null): void => {
  renewer = next;
};

/** The phone engine's shutdown, registered while a phone runs; the logout waits for it. */
export const setShutdownHook = (next: (() => Promise<void>) | null): void => {
  shutdownHook = next;
};

/**
 * Runs the registered shutdown and waits for it, but not longer than `timeoutMs`; a failing or slow
 * shutdown must never keep an agent from signing out.
 */
export const runShutdownHook = async (timeoutMs: number): Promise<void> => {
  const hook = shutdownHook;
  if (!hook) return;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<void>(resolve => {
    timer = setTimeout(resolve, timeoutMs);
  });
  // The hook starts at once; a hook that throws before it returns a promise counts as a failed shutdown.
  let pending: Promise<void>;
  try {
    pending = Promise.resolve(hook());
  } catch {
    pending = Promise.resolve();
  }
  try {
    await Promise.race([pending.catch(() => undefined), timeout]);
  } finally {
    clearTimeout(timer);
  }
};

/** The SHA-256 digest, in hex, of an access token: all the portal keeps to recognise the token the SDK holds. */
export const digestToken = async (accessToken: string): Promise<string> => {
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(accessToken));
  return Array.from(new Uint8Array(digest), byte => byte.toString(16).padStart(2, "0")).join("");
};

/**
 * Whether the SDK already holds the access token with this digest; a renewal that returns it did not
 * renew anything. The portal keeps the digest, never the token itself.
 */
export const isHeldTokenDigest = (digest: string): boolean => heldTokenDigest === digest;

export const holdTokenDigest = (digest: string): void => {
  heldTokenDigest = digest;
};

/** Runs `init` the first time only; concurrent and later callers share its instance. */
export const initCallingOnce = (init: () => Promise<CallingInstance>): Promise<CallingInstance> => {
  if (instance) return Promise.resolve(instance);
  if (!pending) {
    pending = init().then(
      created => {
        instance = created;
        pending = null;
        return created;
      },
      (error: unknown) => {
        pending = null;
        throw error;
      }
    );
  }
  return pending;
};

export const getCallingInstance = (): CallingInstance | null => instance;

/** True from the start of the SDK's initialisation: its token then lives in this page. */
export const hasCallingInstance = (): boolean => instance !== null || pending !== null;

/** Whether the instance finished its own registration (device and mercury); a line registers on its own. */
export const isCallingRegistered = (): boolean => registered;

export const markCallingRegistered = (): void => {
  registered = true;
};

/** After this the instance is deregistered for good: the page has to reload to phone again. */
export const markTornDown = (): void => {
  tornDown = true;
};

export const isTornDown = (): boolean => tornDown;

/** For tests: the state of a fresh page. */
export const resetCallingSession = (): void => {
  instance = null;
  pending = null;
  registered = false;
  tornDown = false;
  renewer = null;
  shutdownHook = null;
  heldTokenDigest = null;
};
